import { createContext, useContext } from 'react';

import type { CoachAnimationState, QuestionValue, SubmitAnswerOptions } from 'bundles/onboarding-common/types';

export type OnboardingQuestionDataContextType = {
  /**
   * The current question value if any, this would be the user's answer to the current question
   */
  questionValue?: QuestionValue | null;
  /**
   * The current coach icon state, this is used to animate the coach icon for the full page onboarding
   */
  coachIconState?: CoachAnimationState;
  /**
   * Set the current question value
   */
  setQuestionValue: (value?: QuestionValue | null) => void;
  /**
   * The function to handle the change of the question value
   */
  onChange: (value: QuestionValue) => void;
  /**
   * Whether the user has answered the current question
   */
  hasAnsweredQuestion: boolean;
  /**
   * Set the has answered question flag
   */
  setHasAnsweredQuestion: (value: boolean) => void;
  /**
   * The validation error message if any
   */
  validationErrorMessage?: string;
  /**
   * Set the validation error message
   */
  setValidationErrorMessage: (value?: string) => void;
  /**
   * Reset the question data, which involves clearing the question value, the has answered question flag, and the validation error message
   */
  resetQuestionData: () => void;
  /**
   * The submit answer options if any, this is used during submission to add additional context to the submission if needed
   */
  submitAnswerOptions?: SubmitAnswerOptions;
  /**
   * Set the submit answer options
   */
  setSubmitAnswerOptions: (value?: SubmitAnswerOptions) => void;
  /**
   * The submit answer error message if any, this is used to display an error message to the user if the submission fails
   */
  submitAnswerErrorMessage?: string;
  /**
   * Set the submit answer error message
   */
  setSubmitAnswerErrorMessage: (value?: string) => void;
};

const OnboardingQuestionDataContext = createContext<OnboardingQuestionDataContextType | null>(null);

export const useOnboardingQuestionData = () => {
  const context = useContext(OnboardingQuestionDataContext);

  if (!context) {
    throw new Error('useOnboardingQuestionData must be used within a OnboardingQuestionDataProvider');
  }

  const {
    questionValue,
    coachIconState,
    setQuestionValue,
    onChange,
    hasAnsweredQuestion,
    setHasAnsweredQuestion,
    validationErrorMessage,
    setValidationErrorMessage,
    resetQuestionData,
    setSubmitAnswerOptions,
    submitAnswerOptions,
    submitAnswerErrorMessage,
    setSubmitAnswerErrorMessage,
  } = context;

  return {
    questionValue,
    coachIconState,
    setQuestionValue,
    onChange,
    hasAnsweredQuestion,
    setHasAnsweredQuestion,
    validationErrorMessage,
    setValidationErrorMessage,
    resetQuestionData,
    setSubmitAnswerOptions,
    submitAnswerOptions,
    submitAnswerErrorMessage,
    setSubmitAnswerErrorMessage,
  };
};

export default OnboardingQuestionDataContext;
